import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";
import { Chat } from "@/components/chat";
import { UpdateModal } from "@/components/UpdateModal";
import { ImageProvider } from "@/contexts/ImageContext";
import { useMsalAuthentication, useMsal } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "./config/auth";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useState, useEffect, useRef } from "react";
import { updatesApi } from "@/services";
import type { AppUpdate } from "@/types";
import { useAuth } from "@/hooks/useAuth";

const IS_MOCK = import.meta.env.VITE_USE_MOCK_API === 'true';
/**
 * This component handles MSAL authentication with BrowserRouter:
 *
 * 1. BrowserRouter manages URL state using HTML5 History API
 * 2. The catch-all route (/*) ensures ProtectedApp renders for any URL
 * 3. MSAL redirect flow:
 *    - User visits app → MSAL redirects to Azure AD
 *    - Azure AD redirects back with auth code in query params
 *    - useMsalAuthentication processes the code and exchanges for tokens
 *    - URL gets cleaned up and user sees the authenticated app
 */

function ProtectedApp() {
  const { accounts } = useMsal();
  const { result, error } = useMsalAuthentication(
    InteractionType.Redirect,
    loginRequest
  );

  const [activeUpdate, setActiveUpdate] = useState<AppUpdate | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const { getAccessToken } = useAuth();
  const fetchedForAccount = useRef<string | null>(null);

  const isAuthenticated = accounts.length > 0 || !!result;
  const accountId = accounts[0]?.homeAccountId ?? result?.account?.homeAccountId ?? null;

  useEffect(() => {
    if (!isAuthenticated || !accountId) return;
    if (fetchedForAccount.current === accountId) return;
    fetchedForAccount.current = accountId;

    let cancelled = false;

    getAccessToken()
      .catch(() => undefined)
      .then(async (token) => {
        const update = await updatesApi.getActiveUpdate(token);
        if (!update || cancelled) return;
        // Pre-fetch the image with auth so the <img> tag doesn't get a 401.
        // On failure imageUrl becomes null so the modal hides the image panel instead of requesting the raw /api path.
        if (update.imageUrl) {
          const { apiClient } = await import('@/services/api');
          const blobUrl = await apiClient.fetchImageAsObjectUrl(update.imageUrl, token ?? undefined);
          if (cancelled) {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            return;
          }
          update.imageUrl = blobUrl;
        }
        if (!cancelled) {
          setActiveUpdate(update);
          setShowUpdateModal(true);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn('[App] Failed to fetch active update:', err);
        fetchedForAccount.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accountId, getAccessToken]);

  const handleUpdateClose = (dismissPermanently: boolean) => {
    setShowUpdateModal(false);
    // Revoke blob URL to free memory
    if (activeUpdate?.imageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(activeUpdate.imageUrl);
    }
    if (dismissPermanently && activeUpdate) {
      getAccessToken()
        .then((token) => updatesApi.dismissUpdate(activeUpdate.id, token))
        .catch((err: unknown) => console.warn('[App] Failed to dismiss update:', err));
    }
  };

  const updateModalEl = showUpdateModal && activeUpdate ? (
    <UpdateModal update={activeUpdate} onClose={handleUpdateClose} />
  ) : null;

  // Handle authentication errors
  if (error) {
    console.error("MSAL Error:", error);
    return <div>Authentication error: {error.message}</div>;
  }

  // Check if user is already authenticated (on page refresh)
  if (accounts.length > 0 && !result) {
    console.log("User already authenticated from existing session");
    return (
      <>
        <ImageProvider>
          <Chat />
        </ImageProvider>
        {updateModalEl}
      </>
    );
  }

  // Authentication successful
  if (result) {
    console.log("Authentication successful via redirect");
    return (
      <>
        <ImageProvider>
          <Chat />
        </ImageProvider>
        {updateModalEl}
      </>
    );
  }

  // Still in authentication flow
  return <div>Signing you in…</div>;
}

function MockApp() {
  const [activeUpdate, setActiveUpdate] = useState<AppUpdate | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    updatesApi.getActiveUpdate().then((update) => {
      if (!cancelled && update) {
        setActiveUpdate(update);
        setShowUpdateModal(true);
      }
    }).catch((err: unknown) => console.warn('[MockApp] Failed to fetch update:', err));
    return () => { cancelled = true; };
  }, []);

  const handleUpdateClose = (dismissPermanently: boolean) => {
    setShowUpdateModal(false);
    if (dismissPermanently && activeUpdate) {
      updatesApi.dismissUpdate(activeUpdate.id).catch((err: unknown) =>
        console.warn('[MockApp] Failed to dismiss update:', err)
      );
    }
  };

  return (
    <>
      <ImageProvider>
        <Chat />
      </ImageProvider>
      {showUpdateModal && activeUpdate && (
        <UpdateModal update={activeUpdate} onClose={handleUpdateClose} />
      )}
    </>
  );
}

export default function App() {
  // initialize your theme (dark/light)
  useTheme();

  return (
    <BrowserRouter>
      <Routes>
        {/* Unauthenticated health check endpoint */}
        <Route path="/health" element={<div>OK</div>} />
        {/* catch-all for both initial load and callback */}
        <Route path="/*" element={IS_MOCK ? <MockApp /> : <ProtectedApp />} />
      </Routes>
      <ToastContainer />
    </BrowserRouter>
  );
}
